// NPM packages
import React from 'react';
import { Typography } from '@mui/material';
import styled from '@emotion/styled';
import {
  faHandHoldingMedical,
  faHeadSideVirus,
  faHeartbeat,
  faLaptopCode,
} from '@fortawesome/free-solid-svg-icons';

// All other imports
import TeamMember from 'types/TeamMember';
import Organization from 'types/Organization';
import TeamMemberRole from 'types/TeamMemberRole';
import MemberGrid from 'components/about/MemberGrid';
import Principles, { Principle } from 'components/about/Principles';
import Link from 'components/core/Link';
import FundingStatement from 'components/core/FundingStatement';
import PageContent from 'components/core/PageContent';
import H1 from 'components/core/H1';

const Root = styled(PageContent)({});

const StyledPrinciples = styled(Principles)({
  marginTop: '3rem',
  marginBottom: '2.5rem',
});

const corePrinciples: Principle[] = [
  {
    title: 'Living',
    description:
      'There should be continuous interaction with the platform throughout the data and code lifecycle rather than only storing the final product.',
    icon: faHeartbeat,
  },
  {
    title: 'Intuitive',
    description:
      'Using the platform should not require a steep learning curve.',
    icon: faHeadSideVirus,
  },
  {
    title: 'Value-adding',
    description:
      'There should be tools built into the platform that enhance the ability of the MSD community to do their work.',
    icon: faHandHoldingMedical,
  },
  {
    title: 'Environment',
    description:
      'The platform should include a computational component that delivers an integrated data-work environment as opposed to a stand-alone data repository.',
    icon: faLaptopCode,
  },
];

const firstPhaseGoals = [
  'Quickly and easily find datasets produced by other users and projects.',
  'Permanently archive small (<250 MB), medium (250 MB - 50 GB), and large (50 GB to 20 TB) final-form datasets and generate data Digital Object Identifiers to meet journal requirements for data sharing.',
  'Use an intuitive web-based user interface to document and share versioned datasets and associate data with the code used to produce it.',
  'Train new team members on MSD projects to effectively manage data and code and capture the institutional knowledge of members that leave a project.',
  'Create and manage teams that cross institutions to quickly and easily grant access to data and code without having to obtain multiple sets of institutional credentials.',
  'Share working datasets across multiple institutions collaborating on a project in real-time.',
];

const orgs: Record<string, Organization> = {
  pnnl: {
    name: 'PNNL',
  },
  nrel: {
    name: 'NREL',
  },
};

const team: TeamMember[] = [
  {
    name: 'Casey Burleyson',
    org: orgs['pnnl'],
    msdLiveRole: TeamMemberRole.PI,
    avatar: 'avatars/casey.png',
  },
  {
    name: 'Carina Lansing',
    org: orgs['pnnl'],
    msdLiveRole: TeamMemberRole.LEAD_ARCHITECT,
    avatar: 'avatars/Carina_Lansing.jpg',
  },
  {
    name: 'Zoë Guillen',
    org: orgs['pnnl'],
    msdLiveRole: TeamMemberRole.DATA_REPO_LEAD,
    avatar: 'avatars/Zoe_Guillen.jpg',
  },
  {
    name: 'Devin McAllester',
    org: orgs['pnnl'],
    msdLiveRole: TeamMemberRole.SOFTWARE_ENGINEER,
    avatar: 'avatars/devin_mcallester.jpeg',
  },
  {
    name: 'Elvis Offor',
    org: orgs['pnnl'],
    msdLiveRole: TeamMemberRole.SOFTWARE_ENGINEER,
    avatar: 'avatars/Elvis_Offor.jpeg',
  },
  {
    name: 'Jon Weers',
    org: orgs['nrel'],
    msdLiveRole: TeamMemberRole.CLOUD_ADVISOR,
    avatar: 'avatars/Jon_Weers.jpeg',
  },
  {
    name: 'Matthew Macduff',
    org: orgs['pnnl'],
    msdLiveRole: TeamMemberRole.SOFTWARE_ENGINEER,
    avatar: 'avatars/Matthew_MacDuff.jpg',
  },
  {
    name: 'Anna Sabin',
    org: orgs['pnnl'],
    msdLiveRole: TeamMemberRole.UX_ENGINEER,
    avatar: 'avatars/anna_sabin.png',
  },
];

const stakeholders: TeamMember[] = [
  {
    name: 'Brent Daniel',
    org: {
      name: 'ICoM',
      website: 'https://icom.pnnl.gov',
    },
  },
  {
    name: 'Klaus Keller',
    org: {
      name: 'PCHES-ADAPT',
      website: 'https://www.pches.psu.edu',
    },
  },
  {
    name: 'Casey McGrath',
    org: {
      name: 'IM3',
      website: 'https://im3.pnnl.gov',
    },
  },
  {
    name: 'Ying Zhang',
    org: {
      name: 'InteRFACE',
      website:
        'https://climatemodeling.science.energy.gov/projects/interface-interdisciplinary-research-arctic-coastal-environments',
    },
  },
  {
    name: 'Rob Nicholas',
    org: {
      name: 'PCHES-ADAPT',
      website: 'https://www.pches.psu.edu',
    },
  },
  {
    name: 'Pralit Patel',
    org: {
      name: 'GCIMS',
      website: 'https://gcims.pnnl.gov',
    },
  },
  {
    name: 'Stefan Rahimi-Esfarjani',
    org: {
      name: 'HyperFACETS',
      website: 'https://hyperfacets.ucdavis.edu',
    },
  },
  {
    name: 'Pat Reed',
    org: {
      name: 'IM3',
      website: 'https://im3.pnnl.gov',
    },
  },
  {
    name: 'Travis Thurber',
    org: {
      name: 'IM3',
      website: 'https://im3.pnnl.gov',
    },
  },
  {
    name: 'Adam Schlosser',
    org: {
      name: 'MIT',
      website: 'https://globalchange.mit.edu/',
    },
  },
  {
    name: 'Chris Vernon',
    org: {
      name: 'IM3',
      website: 'https://im3.pnnl.gov',
    },
  },
];

const content = (
  <>
    <H1>What is MSD-LIVE?</H1>
    <Typography>
      MSD-LIVE, the MultiSector Dynamics – Living, Intuitive, Value-adding,
      Environment, is a flexible and scalable data and code management system
      combined with a distributed computational platform that will enable MSD
      researchers to document and archive their data, run their models and
      analysis tools, and share their data, software, and multi-model workflows
      within a robust{' '}
      <Link to="https://multisectordynamics.org/">Community of Practice</Link>.
      MSD-LIVE will facilitate a new open, collaborative, resource-rich,
      technology-facilitated, community-driven way of doing MSD research.
      MSD-LIVE will accelerate progress, facilitate and incentivize
      collaboration, and enhance the scientific impact and visibility of the MSD
      community. The platform is built around four core principles:
    </Typography>
    <StyledPrinciples principles={corePrinciples} />
    <Typography>
      As in many other communities, MSD researchers struggle with data and code
      management—from finding storage to making it easy for others to find,
      share, and re-use data and code. However, the extraordinary diversity of
      MSD research, encapsulated in the “multi-” descriptor that applies to
      basically every aspect of what we do, exacerbates these challenges and
      demands a state-of-the-science solution. We used the first phase of the
      MSD-LIVE project to develop a cloud-based core data repository that can be
      used to store, share, and document MSD data. After the first phase of the
      MSD-LIVE project, MSD projects will be able to:
    </Typography>
    <ol>
      {firstPhaseGoals.map((goal) => (
        <li key={goal}>{goal}</li>
      ))}
    </ol>
    <Typography variant="h2">Funding</Typography>
    <FundingStatement />
    <Typography variant="h2">Team</Typography>
    <Typography>
      MSD-LIVE is being developed by a team led by scientists at the DOE’s
      Pacific Northwest National Laboratory. The team is composed of MSD
      researchers, data scientists, and software engineers.
    </Typography>
    <MemberGrid members={team} />
    <Typography variant="h2">Stakeholder Group</Typography>
    <Typography>
      MSD-LIVE is supported by an active and valuable stakeholder group made up
      of project representatives from across the MSD community. The stakeholder
      group reviews progress and provides continuous input that can help guide
      development. They also act as liaisons between MSD-LIVE and their
      respective projects.
    </Typography>
    <MemberGrid members={stakeholders} />
  </>
);

export interface AboutProps {
  className?: string;
}

export default function About(props: AboutProps): React.ReactElement | null {
  const { className } = props;
  return <Root className={className}>{content}</Root>;
}
